import { useWorkspaceStore } from '@/stores/use-workspace-store';

type TokenGetter = () => string | null;

let _getToken: TokenGetter = () => null;

const envUrl = import.meta.env.VITE_API_URL as string | undefined;
const API_BASE = envUrl && envUrl.length > 0 ? `${envUrl}/api` : '/api';

/** Configure how apiFetch resolves the auth token. Call once at app startup. */
export function configureAuth(getToken: TokenGetter) {
  _getToken = getToken;
}

/**
 * Typed API error thrown by apiFetch on non-2xx responses.
 * `code` is the stable machine-readable identifier from the server envelope
 * (e.g. "NOT_FOUND", "CONFLICT"). `details` carries any structured payload
 * the server attached (e.g. conflict active-recording info).
 */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function buildHeaders(options: RequestInit): Record<string, string> {
  const token = _getToken();
  // Skip the JSON default when sending FormData — the browser must set
  // Content-Type itself (with the multipart boundary) for the server to parse it.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // M10b: inject workspace context from the M9 selection store.
  // The server-side WorkspaceContext middleware (M10a) honors this header and
  // falls back to personal workspace when absent — preserving backwards-compat
  // for any call sites where activeWorkspaceId hasn't been set yet.
  // An explicit X-Workspace-ID passed by the caller wins over the active
  // default — a call deliberately scoped to a specific workspace (e.g. the
  // personal subscription tab while a team is active) must not be overwritten.
  const workspaceId = useWorkspaceStore.getState().activeWorkspaceId;
  if (workspaceId !== null && headers['X-Workspace-ID'] === undefined) {
    headers['X-Workspace-ID'] = workspaceId;
  }

  return headers;
}

/**
 * Auth-aware raw fetch — returns the Response directly without parsing.
 * Used when the caller needs to read pagination headers or handle errors itself.
 */
export async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: buildHeaders(options),
  });
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: buildHeaders(options),
      signal: options.signal ?? controller.signal,
    });
  } catch (err) {
    // Distinguish our 10s timeout abort from a caller-driven cancellation.
    // Timeout → a real, handleable failure surfaced as a typed ApiError.
    // Caller's signal → an intentional cancel; re-throw the original AbortError
    // so existing `signal.aborted` guards swallow it (Sentry LUMEN-2E).
    if (err instanceof DOMException && err.name === 'AbortError' && controller.signal.aborted) {
      throw new ApiError('TIMEOUT', `Request timed out: ${path}`, 0);
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // 'UNKNOWN' is a backward-compat sentinel: server responded with an old
    // { error: "..." } envelope (pre-normalization) or no structured body.
    throw new ApiError(
      body.code ?? 'UNKNOWN',
      body.message ?? body.error ?? `API error: ${response.status}`,
      response.status,
      body.details,
    );
  }

  // 204 is the canonical empty-body status; Content-Length: 0 also covers
  // any handler that returns an empty body with a non-204 status.
  if (response.status === 204 || response.headers?.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json();
}
